import { z } from 'zod';
import { errorCodeSchema } from '../common/api-error.js';
import { paginationQuerySchema } from '../common/pagination.js';
import { actorSchema, branchRefSchema, businessDateSchema, fuelMoneySchema, productSchema, TRANSACTION_MAX_PAGE_SIZE, } from './primitives.js';
/**
 * وضع الجرد and الحركات المعلقة (docs/rebuild/fuel.md sections 4.4 and 5.12,
 * owner tickets KAN-28 and KAN-42). A purchase, cash sale, debt charge, payment
 * or receipt saved while the mode is on is PENDING (معلقة) and belongs to no
 * shift until it is posted.
 */
export const postingStatusSchema = z.enum(['POSTED', 'PENDING']);
/** The status of the mode for one branch and product. */
export const inventoryModeStatusSchema = z.object({
    product: productSchema,
    active: z.boolean(),
    /** When and by whom the mode was last turned on or off; null when it never was. */
    changedAt: z.iso.datetime({ offset: true }).nullable(),
    changedBy: z.string().nullable(),
});
/** One entry per product the user may access, for the session branch. */
export const inventoryModesSchema = z.array(inventoryModeStatusSchema);
/** PUT /inventory-mode: the main dashboard alone turns the mode on or off (KAN-44). */
export const setInventoryModeSchema = z.object({
    product: productSchema,
    active: z.boolean(),
});
/** The posting part of a purchase, sale or debt movement (details dialog). */
export const postingInfoSchema = z.object({
    status: postingStatusSchema,
    postedAt: z.iso.datetime({ offset: true }).nullable(),
    postedBy: actorSchema.nullable(),
    /** إجبار الترحيل: posted while وضع الجرد was still on, into the shift being counted. */
    forced: z.boolean(),
});
/** PAYMENT and RECEIPT joined with owner ticket KAN-42. */
export const pendingTransactionTypeSchema = z.enum([
    'PURCHASE',
    'SALE',
    'DEBT_SALE',
    'PAYMENT',
    'RECEIPT',
]);
export const pendingTransactionListQuerySchema = paginationQuerySchema.extend({
    pageSize: z.coerce.number().int().positive().max(TRANSACTION_MAX_PAGE_SIZE).default(50),
    /** معلقة by default; ALL lists both. */
    postingStatus: z.enum(['PENDING', 'POSTED', 'ALL']).default('PENDING'),
    type: pendingTransactionTypeSchema.optional(),
    product: productSchema.optional(),
    branchId: z.string().optional(),
});
/** One row of الحركات المعلقة (section 5.12). */
export const pendingTransactionRowSchema = z.object({
    id: z.string(),
    type: pendingTransactionTypeSchema,
    number: z.number().int(),
    reference: z.string(),
    product: productSchema,
    branch: branchRefSchema,
    /** The debtor, the PDA purchase customer, the person of a payment or receipt, or null. */
    customerName: z.string().nullable(),
    /** Null for a payment or receipt, which carries no quantities. */
    barrels: z.string().nullable(),
    jerrycans: z.string().nullable(),
    /** The final total, or the amount of a payment or receipt. */
    finalTotal: fuelMoneySchema,
    /** PENDING only for a PDA purchase still waiting for its prices. */
    priceStatus: z.enum(['PRICED', 'PENDING']),
    completed: z.boolean(),
    /** The entry time, never changed by posting. */
    createdAt: z.iso.datetime({ offset: true }),
    /**
     * وقت الإدخال shows this business date (the shift open at entry) with the
     * time of `createdAt` (KAN-40), never the calendar date. Kept after posting.
     */
    entryBusinessDate: businessDateSchema,
    createdBy: actorSchema,
    /** موظف الإكمال and its time; null until completed. */
    completedBy: actorSchema.nullable(),
    completedAt: z.iso.datetime({ offset: true }).nullable(),
    postingStatus: postingStatusSchema,
    /** The entry business date while pending; the posting business date once posted. */
    businessDate: businessDateSchema,
    postedAt: z.iso.datetime({ offset: true }).nullable(),
    postedBy: actorSchema.nullable(),
    /** Posted while وضع الجرد was still on (إجبار الترحيل). */
    forcedPost: z.boolean(),
});
export const pendingTransactionListSchema = z.object({
    rows: z.array(pendingTransactionRowSchema),
    pagination: z.object({
        page: z.number().int().positive(),
        pageSize: z.number().int().positive(),
        total: z.number().int().nonnegative(),
        totalPages: z.number().int().nonnegative(),
    }),
});
/**
 * GET /pending-transactions/:type/:id/post-preview: what the posting
 * confirmation shows (client answer, KAN-28): the number, the entry time and
 * the business date (shift) it would go to now. `businessDate` is null when no
 * shift is open for the product; `inventoryModeActive` asks for إجبار الترحيل.
 */
export const postPreviewSchema = z.object({
    id: z.string(),
    type: pendingTransactionTypeSchema,
    number: z.number().int(),
    reference: z.string(),
    product: productSchema,
    createdAt: z.iso.datetime({ offset: true }),
    /** The business date at entry, shown with the time of `createdAt` (KAN-40). */
    entryBusinessDate: businessDateSchema,
    businessDate: businessDateSchema.nullable(),
    inventoryModeActive: z.boolean(),
});
/** POST /pending-transactions/:type/:id/post body: `force` is required while the mode is on. */
export const postTransactionSchema = z.object({ force: z.boolean().default(false) });
/** POST /pending-transactions/:type/:id/post result. */
export const postTransactionResultSchema = z.object({
    id: z.string(),
    reference: z.string(),
    postingStatus: z.literal('POSTED'),
    businessDate: businessDateSchema,
    postedAt: z.iso.datetime({ offset: true }),
    forced: z.boolean(),
});
// --- bulk posting (ترحيل المحدد, owner ticket KAN-41) -------------------------
/** One selected row of الحركات المعلقة. */
export const pendingTransactionRefSchema = z.object({
    type: pendingTransactionTypeSchema,
    id: z.string().min(1),
});
/** At most one page of rows, without duplicates. */
const selectedItemsSchema = z
    .array(pendingTransactionRefSchema)
    .min(1)
    .max(TRANSACTION_MAX_PAGE_SIZE)
    .refine((items) => new Set(items.map((i) => `${i.type}:${i.id}`)).size === items.length, {
    message: 'Each transaction may be selected once',
});
/** POST /pending-transactions/post-preview body. */
export const bulkPostPreviewInputSchema = z.object({ items: selectedItemsSchema });
/**
 * POST /pending-transactions/post-preview: what posting the selection would do
 * now. The rows that can be posted are grouped by branch and product with the
 * business date they would go to and whether وضع الجرد is on (a forced post);
 * the others are listed with the refusal the post itself would give.
 */
export const bulkPostPreviewSchema = z.object({
    total: z.number().int().nonnegative(),
    groups: z.array(z.object({
        branch: branchRefSchema,
        product: productSchema,
        businessDate: businessDateSchema,
        inventoryModeActive: z.boolean(),
        count: z.number().int().positive(),
    })),
    refused: z.array(z.object({
        type: pendingTransactionTypeSchema,
        id: z.string(),
        /** Null when the transaction does not exist (or was deleted). */
        reference: z.string().nullable(),
        code: errorCodeSchema,
    })),
});
/** POST /pending-transactions/post body: `force` covers the rows whose mode is on. */
export const bulkPostInputSchema = z.object({
    items: selectedItemsSchema,
    force: z.boolean().default(false),
});
/**
 * POST /pending-transactions/post result: each row is posted on its own, exactly
 * as a single ترحيل, so a refused row stays pending and the others still post.
 */
export const bulkPostResultSchema = z.object({
    posted: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    results: z.array(z.object({
        type: pendingTransactionTypeSchema,
        id: z.string(),
        /** Null when refused: the client names the row from its selection. */
        reference: z.string().nullable(),
        ok: z.boolean(),
        /** The business date it was posted to; null when refused. */
        businessDate: businessDateSchema.nullable(),
        forced: z.boolean(),
        /** The refusal; null when posted. */
        code: errorCodeSchema.nullable(),
    })),
});
//# sourceMappingURL=inventory.contract.js.map